import { ErrorHandler, Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * App-wide safety net.
 *
 * Angular's default ErrorHandler rethrows/logs uncaught errors as red console
 * errors. A single non-fatal error thrown inside a component lifecycle hook
 * (e.g. accessing a ViewChild's nativeElement before it renders, a late API
 * field being undefined, a third-party widget failing) then shows up as a scary
 * error and, during change detection, can leave the view in a half-initialised
 * state.
 *
 * This handler CONTAINS those errors: the page keeps working, and the message is
 * logged compactly (as a warning, not an uncaught error) so it stays debuggable
 * without alarming users who open the console. Fix the root causes where it
 * matters — this just guarantees one stray error can never break a page.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  handleError(error: any): void {
    const message = (error && (error.message || error.toString())) || 'Unknown error';

    // Only log in the browser; on the server (SSR/prerender) stay silent so a
    // contained component error doesn't pollute build logs or fail a render.
    if (isPlatformBrowser(this.platformId)) {
      // console.warn (not console.error) keeps the console calm; the message is
      // still here for debugging.
      console.warn('[contained]', message);
    }
    // Intentionally do NOT rethrow — containment is the whole point.
  }
}
